<template>
  <el-dialog
    title="查看经销商"
    :visible.sync="visible"
    width="960px"
    append-to-body
    class="dealer-view-dialog"
    @close="handleClose"
  >
    <div class="dealer-view-dialog__search">
      <div class="search-item">
        <el-input
          v-model="searchForm.dealer_id"
          placeholder="经销商ID"
          clearable
          size="small"
        />
      </div>
      <div class="search-item">
        <el-input
          v-model="searchForm.hqid"
          placeholder="分销商HQID"
          clearable
          size="small"
        />
      </div>
      <div class="search-item">
        <el-input
          v-model="searchForm.vad"
          placeholder="分销商VAD"
          clearable
          size="small"
        />
      </div>
      <div class="search-item">
        <el-input
          v-model="searchForm.rtm"
          placeholder="渠道RTM"
          clearable
          size="small"
        />
      </div>
      <el-select
        v-model="searchForm.status"
        placeholder="状态"
        clearable
        size="small"
        class="search-status"
      >
        <el-option
          v-for="item in statusOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <el-button type="primary" size="small" class="search-btn" @click="handleSearch">搜索</el-button>
      <el-button
        size="small"
        :loading="retryAllLoading"
        :disabled="createPending"
        @click="handleRetryAll"
      >重试全部</el-button>
    </div>

    <div class="dealer-view-dialog__progress">
      <span>汇付创建中 {{ progress.creating }}</span>
      <span>创建失败 {{ progress.failed }}</span>
      <span>未发布 {{ progress.unpublished }}</span>
      <span>进行中 {{ progress.processing }}</span>
      <span>已下架 {{ progress.offline }}</span>
      <i
        class="progress-refresh"
        :class="progressLoading ? 'el-icon-loading' : 'el-icon-refresh'"
        @click="loadProgress"
      />
    </div>

    <el-table
      v-loading="loading"
      :data="tableData"
      border
      stripe
      class="dealer-view-dialog__table"
    >
      <el-table-column prop="dealer_name" label="经销商名称" min-width="200" />
      <el-table-column prop="hqid" label="分销商HQID" min-width="120" />
      <el-table-column prop="vad" label="分销商VAD" min-width="100" />
      <el-table-column prop="rtm" label="渠道RTM" min-width="100" />
      <el-table-column label="状态" min-width="120">
        <template slot-scope="{ row }">
          <span class="status-text" :class="statusClass(row)">{{ statusText(row) }}</span>
          <el-tooltip
            v-if="row.create_status === 'failed'"
            :content="formatFailureTooltip(row)"
            placement="top"
          >
            <i class="el-icon-question failure-tip" />
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="操作" min-width="100">
        <template slot-scope="{ row }">
          <el-link
            v-if="row.create_status !== 'success'"
            type="primary"
            :underline="false"
            :disabled="createPending || row.retrying"
            @click="handleRetry(row)"
          >重试</el-link>
          <el-link
            v-else-if="row.status === 'not_created'"
            type="primary"
            :underline="false"
            :disabled="createPending || row.statusChanging"
            @click="handleDealerOnline(row)"
          >上架</el-link>
          <el-button
            v-else-if="row.status === 'online'"
            type="danger"
            plain
            size="mini"
            :loading="row.statusChanging"
            @click="handleTerminate(row)"
          >终止</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="dealer-view-dialog__pagination">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page.sync="pagination.page"
        :page-size.sync="pagination.pageSize"
        :page-sizes="[10, 20, 50]"
        :total="pagination.total"
        @size-change="handlePageChange"
        @current-change="handlePageChange"
      />
    </div>

    <div slot="footer" class="dealer-view-dialog__footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getActivityDealers, getDealerProgress, retryActivityDealer, retryAllActivityDealers, onlineActivityDealer, offlineActivityDealer } from '../api'

export default {
  name: 'DealerViewDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    activity: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: false,
      retryAllLoading: false,
      progressLoading: false,
      tableData: [],
      progress: {
        creating: 0,
        failed: 0,
        unpublished: 0,
        processing: 0,
        offline: 0
      },
      statusOptions: [
        { label: '全部', value: 'all' },
        { label: '创建失败', value: 'failed' },
        { label: '未发布', value: 'unpublished' },
        { label: '进行中', value: 'processing' },
        { label: '已下架', value: 'offline' }
      ],
      searchForm: {
        dealer_id: '',
        hqid: '',
        vad: '',
        rtm: '',
        status: 'all'
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      }
    }
  },
  computed: {
    visible: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    createPending () {
      return this.progress.creating > 0
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.resetDialog()
      }
    }
  },
  methods: {
    formatFailureTooltip (row) {
      const code = row.last_error_code || '-'
      const reason = row.last_error_msg || '未知原因'
      return `错误码：${code}；失败原因：${reason}`
    },
    mapDealerRow (item) {
      const notCreated = item.create_status !== 'success'
      return {
        ...item,
        dealer_name: item.dealer_name_snapshot || item.dealer_name || '',
        hqid: item.hqid || item.huifu_id || '',
        vad: item.vad || '',
        rtm: item.rtm || '',
        online: item.status === 'online',
        notCreated
      }
    },
    buildQueryParams () {
      return {
        page: String(this.pagination.page),
        pageSize: String(this.pagination.pageSize),
        dealer_id: this.searchForm.dealer_id || '',
        hqid: this.searchForm.hqid || '',
        vad: this.searchForm.vad || '',
        rtm: this.searchForm.rtm || '',
        status: !this.searchForm.status || this.searchForm.status === 'all' ? '' : this.searchForm.status
      }
    },
    resetDialog () {
      this.searchForm = {
        dealer_id: '',
        hqid: '',
        vad: '',
        rtm: '',
        status: 'all'
      }
      this.pagination.page = 1
      this.pagination.pageSize = 20
      this.loadTableData()
      this.loadProgress()
    },
    statusText (row) {
      if (row.create_status === 'pending') return '汇付创建中'
      if (row.create_status !== 'success') return row.create_status_text || '创建失败'
      if (row.status === 'online') return '进行中'
      if (row.status === 'offline') return '已下架'
      return row.status_text || '未发布'
    },
    statusClass (row) {
      if (row.create_status === 'pending') return 'is-creating'
      if (row.create_status !== 'success') return 'is-failed'
      if (row.status === 'online') return 'is-online'
      if (row.status === 'offline') return 'is-offline'
      return ''
    },
    async loadProgress () {
      const activityId = this.activity && this.activity.activity_id
      if (!activityId || this.progressLoading) return
      this.progressLoading = true
      try {
        const res = await getDealerProgress(activityId)
        this.progress = Object.assign({
          creating: 0,
          failed: 0,
          unpublished: 0,
          processing: 0,
          offline: 0
        }, res.data.data || {})
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '获取进度失败'
        this.$message.error(msg)
      } finally {
        this.progressLoading = false
      }
    },
    async loadTableData () {
      const activityId = this.activity && this.activity.activity_id
      if (!activityId) {
        this.tableData = []
        this.pagination.total = 0
        return
      }
      this.loading = true
      try {
        const res = await getActivityDealers(activityId, this.buildQueryParams())
        const data = res.data.data || {}
        const list = data.list || []
        this.tableData = list.map(item => this.mapDealerRow(item))
        this.pagination.total = data.total_count || 0
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '获取经销商列表失败'
        this.$message.error(msg)
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch () {
      this.pagination.page = 1
      this.loadTableData()
    },
    handlePageChange () {
      this.loadTableData()
    },
    async handleDealerOnline (row) {
      const activityId = this.activity && this.activity.activity_id
      const dealerId = row.dealer_id
      if (!activityId || !dealerId) {
        this.$message.warning('缺少活动或经销商信息')
        return
      }
      if (this.createPending || row.statusChanging) return
      this.$set(row, 'statusChanging', true)
      try {
        await onlineActivityDealer(activityId, dealerId)
        this.$message.success(`${row.dealer_name} 上架成功`)
        this.loadTableData()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '上架失败'
        this.$message.error(msg)
      } finally {
        this.$set(row, 'statusChanging', false)
      }
    },
    async handleTerminate (row) {
      const activityId = this.activity && this.activity.activity_id
      const dealerId = row.dealer_id
      if (!activityId || !dealerId) {
        this.$message.warning('缺少活动或经销商信息')
        return
      }
      if (row.statusChanging) return
      try {
        await this.$confirm(`确定终止经销商「${row.dealer_name}」的活动？终止后将不允许再次上架。`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      this.$set(row, 'statusChanging', true)
      try {
        await offlineActivityDealer(activityId, dealerId)
        this.$message.success(`${row.dealer_name} 已终止`)
        this.loadTableData()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '终止失败'
        this.$message.error(msg)
      } finally {
        this.$set(row, 'statusChanging', false)
      }
    },
    async handleRetry (row) {
      const activityId = this.activity && this.activity.activity_id
      const dealerId = row.dealer_id
      if (!activityId || !dealerId) {
        this.$message.warning('缺少活动或经销商信息')
        return
      }
      if (this.createPending || row.retrying) return
      this.$set(row, 'retrying', true)
      try {
        const res = await retryActivityDealer(activityId, dealerId)
        const data = res.data.data || {}
        const { processed = 0 } = data
        if (processed > 0) {
          this.$message.success(`${row.dealer_name} 重试成功`)
          this.loadTableData()
        } else {
          this.$message.warning(`${row.dealer_name} 重试未处理`)
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '重试失败'
        this.$message.error(msg)
      } finally {
        this.$set(row, 'retrying', false)
      }
    },
    async handleRetryAll () {
      const activityId = this.activity && this.activity.activity_id
      if (!activityId) {
        this.$message.warning('缺少活动信息')
        return
      }
      if (this.createPending || this.retryAllLoading) return
      try {
        await this.$confirm('确定重试全部未创建成功的经销商？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      this.retryAllLoading = true
      try {
        const res = await retryAllActivityDealers(activityId)
        const data = res.data.data || {}
        const { processed = 0 } = data
        if (processed > 0) {
          this.$message.success(`重试完成，已处理 ${processed} 家经销商`)
          this.loadTableData()
        } else {
          this.$message.warning('暂无需要重试的经销商')
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '重试全部失败'
        this.$message.error(msg)
      } finally {
        this.retryAllLoading = false
      }
    },
    handleClose () {
      this.visible = false
      this.$emit('close')
    },
    handleConfirm () {
      this.handleClose()
    }
  }
}
</script>

<style scoped lang="scss">
.dealer-view-dialog {
  &__search {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 12px 16px;
    background: #f6f7f9;

    .search-item {
      flex: 1;
      min-width: 0;
    }

    .search-btn {
      flex-shrink: 0;
    }

    .search-status {
      width: 120px;
      flex-shrink: 0;
    }
  }

  &__progress {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;
    color: #606266;

    .progress-refresh {
      cursor: pointer;
      color: #409eff;
    }
  }

  &__table {
    width: 100%;
  }

  &__pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }

  &__footer {
    text-align: right;
  }

  .status-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .failure-tip {
    color: #e6a23c;
    cursor: help;
  }

  .retry-link {
    margin-left: auto;
  }

  .status-text {
    font-size: 14px;
    color: #606266;

    &.is-creating {
      color: #409eff;
    }

    &.is-failed {
      color: #f56c6c;
    }

    &.is-online {
      color: #67c23a;
    }

    &.is-offline {
      color: #909399;
    }
  }
}

::v-deep .dealer-view-dialog__search {
  .el-input {
    width: 100%;
  }
}
</style>
